import React from 'react'
import * as AppDefinitions from '../../../App.d'
import {
	AccountXeroOrg,
	Calendar,
	Calendar as CalendarData,
	EmployeeLeaveBalance,
	LeaveApplication,
	LeaveType,
} from '../../../../../back-end/utilities/apiDefinitions'
import { FormGroup } from 'react-bootstrap'
import * as RecordStatus from '../../../types/recordStatus'

// components
import * as Messages from '../../ui/messages/messages'
import LoadingImage from '../../../images/Finlert-loading-GIF_100px.gif'
import TeamDemographicsComponent from './teamDemographics'
import LeaveOverviewComponent from './leaveOverview'
import EmployeePerformanceOverview from './employeeOverview'

// utillites
import * as Request from '../../../utilities/request'
import moment from 'moment'
import { xeroTimezoneToIANA } from '../../common/xeroTimezoneToIANA'

// Function to lighten or darken the color
const adjustColorBrightness = (hex: string, factor: number) => {
	let r = parseInt(hex.slice(1, 3), 16)
	let g = parseInt(hex.slice(3, 5), 16)
	let b = parseInt(hex.slice(5, 7), 16)

	// Adjust the color by the factor (e.g., -20 to darken, 20 to lighten)
	r = Math.min(255, Math.max(0, r + factor))
	g = Math.min(255, Math.max(0, g + factor))
	b = Math.min(255, Math.max(0, b + factor))

	return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1).toUpperCase()}`
}

const pieColors = ['#86A581', '#E4B1F7', '#F89995', '#F8C950', '#8AA6FF']

export const calculateUpcomingLeave = (totalFutureLeave: number, leaveApp: LeaveApplication) =>
	(totalFutureLeave += Number((moment(leaveApp.endDate!).valueOf() + 1000 * 3600 * 24 - moment(leaveApp.startDate!).valueOf()) / (1000 * 3600 * 24)) || 1) //we add a day in the difference so the end date is included

// eslint-disable-next-line
export const getDataForPieChart = (object: { [x: string]: any }, colorIndex: number) => {
	const data: { title: string; value: number; color: string }[] = []
	Object.keys(object).forEach((key: string, index: number) => {
		if (object[key] !== 0) {
			data.push({
				title: key,
				value: object[key],
				color: adjustColorBrightness(pieColors[colorIndex], 10 * (index + 1)),
			})
		}
	})
	return data
}

interface ObjectDetails {
	amount?: number
	name: string
	days?: number
}
interface DataObject {
	total?: number
	[key: string]: number | ObjectDetails | undefined
}
interface ChartData {
	type: 'bar'
	marker: { color: string }
	x: (number | string)[]
	y: (number | string)[]
	orientation: 'h'
	options: {
		scales: {
			y: { beginAtZero: boolean }
			x: { beginAtZero: boolean }
		}
	}
}

// eslint-disable-next-line
export const getDataForBarChart = (object: DataObject, colorIndex: number, showLeaveInDays: boolean, hoursInDay: number): ChartData => {
	const data: ChartData = {
		type: 'bar',
		marker: { color: pieColors[colorIndex] },
		x: [],
		y: [],
		orientation: 'h',
		options: {
			scales: {
				y: {
					beginAtZero: true,
				},
				x: {
					beginAtZero: true,
				},
			},
		},
	}
	Object.keys(object).forEach((key: string) => {
		const value = object[key]
		if (key === 'total') {
			data.y.push('Total')
			data.x.push(showLeaveInDays ? parseFloat(Math.fround((value as number) / hoursInDay).toFixed(2)) : (value as number))
		} else {
			if (typeof value === 'object' && value !== null && 'name' in value) {
				data.y.push(value.name)
				if (value.amount) {
					data.x.push(value.amount)
				} else {
					data.x.push(showLeaveInDays ? parseFloat(Math.fround(value.days! / hoursInDay).toFixed(2)) : value.days!)
				}
			} else {
				data.y.push(key)
				data.x.push(value!)
			}
		}
	})
	return { ...data, x: data.x.reverse(), y: data.y.reverse() }
}

export const isLeaveWithinPeriod = (leave: LeaveApplication, period: { startDate: moment.MomentInput; endDate: moment.MomentInput }): boolean => {
	const leaveStart = moment.utc(leave.startDate)
	const leaveEnd = moment.utc(leave.endDate)
	const periodStart = moment.utc(period.startDate).startOf('day')
	const periodEnd = moment.utc(period.endDate).endOf('day')

	return (
		leaveStart.isBetween(periodStart, periodEnd, undefined, '[]') || // starts in period
		leaveEnd.isBetween(periodStart, periodEnd, undefined, '[]') || // ends in period
		(leaveStart.isBefore(periodStart) && leaveEnd.isAfter(periodEnd)) // spans entire period
	)
}
export const isWorkingDay = (date: moment.Moment): boolean => {
	const day = date.day()
	return day !== 0 && day !== 6 // Sunday = 0, Saturday = 6
}

export const getWorkingDaysBetween = (start: moment.Moment, end: moment.Moment): moment.Moment[] => {
	const days: moment.Moment[] = []
	const current = start.clone()

	while (current.isSameOrBefore(end, 'day')) {
		if (isWorkingDay(current)) {
			days.push(current.clone())
		}
		current.add(1, 'day')
	}
	return days
}

export const leaveApplicationIncludedUnits = (currentOrg: AccountXeroOrg, leaveApp: LeaveApplication, periodStart: moment.Moment, periodEnd: moment.Moment) => {
	if (!leaveApp.leavePeriod || !leaveApp.startDate || !leaveApp.endDate) {
		return 0
	}

	const leaveStart = moment(leaveApp.startDate).startOf('day')
	const leaveEnd = moment(leaveApp.endDate).endOf('day')
	const allWorkingDays = getWorkingDaysBetween(leaveStart, leaveEnd)

	const includedWorkingDays = allWorkingDays.filter((date) => date.isSameOrAfter(periodStart, 'day') && date.isSameOrBefore(periodEnd, 'day'))

	if (includedWorkingDays.length === 0) {
		return 0
	}

	// Total units for the whole leave
	const totalLeaveUnits = leaveApp.leavePeriod.reduce((sum, p) => sum + (p.numberOfUnits || 0), 0)

	// All days in range are within reporting period — return total directly
	if (allWorkingDays.length === includedWorkingDays.length) {
		return totalLeaveUnits
	}

	// Partial range — compute hours proportionally
	const hoursPerDay = currentOrg?.xeroOrg?.hoursInDay ?? totalLeaveUnits / allWorkingDays.length

	const includedUnits = hoursPerDay * includedWorkingDays.length

	return includedUnits
}

export const getCalendarsWithBalances = (
	calendar: Calendar,
	leave: LeaveApplication[],
	leaveBalance: EmployeeLeaveBalance[],
	timezone: string,
	start?: Date,
	end?: Date
) => {
	const todaysDate = moment().tz(xeroTimezoneToIANA[timezone])

	return {
		...calendar,
		employees: calendar.employees
			? calendar.employees
					.map((emp) => {
						const employeeLeave = leave.filter((leaveApp) => emp.employeeID === leaveApp.employeeID)
						const fromTodayOnwardsLeaves =
							start && end
								? employeeLeave.filter((leaveApp) => isLeaveWithinPeriod(leaveApp, { startDate: moment(start), endDate: moment(end) }))
								: employeeLeave.filter((leaveApp) => moment(leaveApp.startDate!).isAfter(todaysDate))
						const associatedBalances = leaveBalance.filter((leaveBalance) => emp.employeeID === leaveBalance.employeeID)

						return {
							...emp,
							daysSinceLastLeave: 0, //not important here
							leaveBalance: Array.from(
								new Set([
									...associatedBalances.map((bal) => bal.xeroOrgLeaveTypeID),
									...fromTodayOnwardsLeaves.map((leaveApp) => leaveApp.xeroOrgLeaveTypeID),
								])
							)
								.map((leaveTypeId) => {
									const matchingBalance = associatedBalances.find((bal) => bal.xeroOrgLeaveTypeID === leaveTypeId)
									const totalFutureLeave = fromTodayOnwardsLeaves.filter((leaveApp) => leaveApp.xeroOrgLeaveTypeID === leaveTypeId)

									if (matchingBalance) {
										return {
											...matchingBalance,
											correspondingLeaves: totalFutureLeave,
											upcoming: totalFutureLeave.reduce(calculateUpcomingLeave, 0),
										}
									}

									// fallback object to match shape of EmployeeLeaveBalance
									const leaveType = calendar.leaveTypes?.find((lt) => lt.xeroOrgLeaveTypeID === leaveTypeId)

									return {
										employee: emp,
										xeroOrgLeaveTypeID: leaveTypeId,
										leaveTypeName: leaveType?.name,
										leaveTypeUnitType: leaveType?.unitType,
										openingBalance: 0,
										currentBalance: 0,
										value: 0, // default
										correspondingLeaves: totalFutureLeave,
										upcoming: totalFutureLeave.reduce(calculateUpcomingLeave, 0),
									}
								})
								.sort((a, b) => a.leaveTypeName!.localeCompare(b.leaveTypeName!)),
						}
					})
					.filter((emp) => emp.leaveBalance.length > 0)
					.sort((a, b) => (a.firstName === b.firstName ? a.lastName!.localeCompare(b.lastName!) : a.firstName!.localeCompare(b.firstName!)))
			: [],
	}
}

interface OverviewComponentFrameProps {
	show: boolean
	appState: AppDefinitions.AppState
	currentOrg: AccountXeroOrg
	currentCalendar: CalendarData
	xeroOrgCalendars: CalendarData[] | null
	pageStatus: string
	updatePageStatus: React.Dispatch<React.SetStateAction<string>>
}

const OverviewComponent = (props: OverviewComponentFrameProps) => {
	const [messages, updateMessages] = Messages.useMessageReducer([])
	const [allLeaveApplications, updateAllLeaveApplications] = React.useState<LeaveApplication[] | []>([])
	const [allXeroOrgLeaveApplications, updateAllXeroOrgLeaveApplications] = React.useState<LeaveApplication[] | []>([])
	const [originalBalanceData, updateOriginalBalanceData] = React.useState<EmployeeLeaveBalance[]>([])
	const [originalXeroBalanceData, updateOriginalXeroBalanceData] = React.useState<EmployeeLeaveBalance[]>([])

	React.useEffect(
		() => {
			const fetchData = async () => {
				const uniqueEmployees = props.currentCalendar.employees!.map((e) => e.employeeID!)
				const leaveTypeIDs = props.currentCalendar.leaveTypes!.map((clt) => clt.xeroOrgLeaveTypeID)
				const [nonXeroLeaveTypes, calendarLeaveTypesData] = await Promise.all([
					Request.get(
						`leavetype?where=XeroOrgID==${props.currentOrg.xeroOrg!.xeroOrgID}&where=XeroOrgLeaveTypeIsNonXero==1`,
						props.appState.authState
					),
					Request.get(`calendar/${props.currentCalendar.calendarID}/leavetype`, props.appState.authState),
				])

				const nonXeroTypeIDs = nonXeroLeaveTypes.data.leaveTypes
					.filter(
						(lt: LeaveType) =>
							lt.isActive &&
							lt.xeroOrgID === props.currentOrg.xeroOrg!.xeroOrgID &&
							calendarLeaveTypesData.data.calendarLeaveTypes.some(
								(clt: { xeroOrgLeaveTypeID: string }) => clt.xeroOrgLeaveTypeID === lt.xeroOrgLeaveTypeID
							)
					)
					.map((clt: { xeroOrgLeaveTypeID: string }) => clt.xeroOrgLeaveTypeID)
				const allNonXeroTypeIDs = nonXeroLeaveTypes.data.leaveTypes
					.filter((lt: LeaveType) => lt.isActive && lt.xeroOrgID === props.currentOrg.xeroOrg!.xeroOrgID)
					.map((clt: { xeroOrgLeaveTypeID: string }) => clt.xeroOrgLeaveTypeID)
				const allXeroEmployeeIDs = [
					...new Set(
						props.xeroOrgCalendars!.reduce(
							(employeeIDs: string[], currentCal) => employeeIDs.concat(currentCal.employees!.map((e) => e.employeeID!)),
							[]
						)
					),
				]
				const allXeroLeaveTypeIDs = [
					...new Set(
						props.xeroOrgCalendars!.reduce(
							(leaveTypeIDs: string[], currentCal) => leaveTypeIDs.concat(currentCal.leaveTypes!.map((clt) => clt.xeroOrgLeaveTypeID!)),
							[]
						)
					),
				]

				const [leaveApp, xeroOrgLeaveApp, leaveBalanceData, leaveBalanceXeroData] = await Promise.all([
					Request.get(
						`leave?where=EmployeeID=in(${uniqueEmployees.join(',')})&where=XeroOrgLeaveTypeID=in(${leaveTypeIDs
							.concat(nonXeroTypeIDs)
							.join(',')})&where=RecordStatusID=in(${[RecordStatus.Active, RecordStatus.Archived].join(',')})&minify`,
						props.appState.authState
					),
					Request.get(
						`leave?where=EmployeeID=in(${allXeroEmployeeIDs.join(',')})&where=XeroOrgLeaveTypeID=in(${allXeroLeaveTypeIDs
							.concat(allNonXeroTypeIDs)
							.join(',')})&where=RecordStatusID=in(${[RecordStatus.Active, RecordStatus.Archived].join(',')})&minify`,
						props.appState.authState
					),
					Request.get(`leavebalance?where=EmployeeID=in(${uniqueEmployees.join(',')})`, props.appState.authState),
					Request.get(`leavebalance?where=EmployeeID=in(${allXeroEmployeeIDs.join(',')})`, props.appState.authState),
				])

				const activeEmployees = props.currentCalendar.employees!.filter((e) => e.isActive).map((e) => e.employeeID!)
				const allXeroActiveEmployees = props.xeroOrgCalendars!.reduce(
					(employeeIDs: string[], currentCal) => employeeIDs.concat(currentCal.employees!.filter((e) => e.isActive).map((e) => e.employeeID!)),
					[]
				)
				const leaves =
					leaveApp.data.leaveApplications.length > 0
						? leaveApp.data.leaveApplications
								.filter((lv: LeaveApplication) => activeEmployees.includes(lv.employeeID!))
								.map((leave: LeaveApplication) => leave)
						: []
				const allXeroOrgLeaves =
					xeroOrgLeaveApp.data.leaveApplications.length > 0
						? xeroOrgLeaveApp.data.leaveApplications
								.filter((lv: LeaveApplication) => allXeroActiveEmployees.includes(lv.employeeID!))
								.map((leave: LeaveApplication) => leave)
						: []
				updateAllLeaveApplications(leaves)
				updateAllXeroOrgLeaveApplications(allXeroOrgLeaves)
				updateOriginalBalanceData(leaveBalanceData.data.employeeLeaveBalances)
				updateOriginalXeroBalanceData(leaveBalanceXeroData.data.employeeLeaveBalances)
				if (props.show) {
					props.updatePageStatus('Finished')
				}
			}

			if (props.currentCalendar.calendarID !== '' && props.show === true) {
				fetchData()
			}
		},
		// eslint-disable-next-line
		[props.appState.authState, props.currentCalendar.calendarID, props.currentOrg, props.show]
	)

	if (!props.show) {
		return null
	}
	if (props.pageStatus !== 'Finished') {
		return (
			<div className="loading-gif">
				<img src={LoadingImage} alt="Loading ..." />
			</div>
		)
	}
	return (
		<>
			<FormGroup style={{ padding: '30px' }}>
				<TeamDemographicsComponent
					allLeaveApplications={allLeaveApplications}
					originalBalanceData={originalBalanceData}
					appState={props.appState}
					currentOrg={props.currentOrg}
					currentCalendar={props.currentCalendar}
				/>
				<LeaveOverviewComponent
					allLeaveApplications={allLeaveApplications}
					originalBalanceData={originalBalanceData}
					appState={props.appState}
					currentOrg={props.currentOrg}
					currentCalendar={props.currentCalendar}
				/>
				<EmployeePerformanceOverview
					allLeaveApplications={allLeaveApplications}
					allXeroOrgLeaveApplications={allXeroOrgLeaveApplications}
					originalBalanceData={originalBalanceData}
					originalXeroOrgBalanceData={originalXeroBalanceData}
					appState={props.appState}
					currentOrg={props.currentOrg}
					xeroOrgCalendars={props.xeroOrgCalendars}
					currentCalendar={props.currentCalendar}
				/>
			</FormGroup>
			<Messages.Messages messages={messages} updateMessage={updateMessages} />
		</>
	)
}

export default OverviewComponent
